import React, { useRef, useState } from 'react';
import axios from 'axios';
import { useOutsideClick } from 'common/utils';
import ConfirmDialog from 'components/common/ConfirmDialog';
import Icon from 'components/common/Icon';
import ReportBox from 'components/common/ReportBox';
import useGlobalStore from 'store/global';
import useStore from 'store/timeline';

import styles from './ThreeDotsMenu.module.scss';

export type Props = {
  postId: number;
  commentId: number;
  ownerId: number;
  isOwnPost?: boolean;
  isOwnComment: boolean;
  parentId?: number;
};

export default function ThreeDotsMenu({
  postId,
  commentId,
  ownerId,
  isOwnPost = false,
  isOwnComment,
  parentId,
}: Props): JSX.Element {
  const setSnackbarMessage = useGlobalStore(
    (state) => state.setSnackbarMessage,
  );
  const [show, setShow] = useState(false);
  const refetchPostComments = useStore((state) => state.refetchPostComments);
  const [openConfirmReportComment, setOpenConfirmReportComment] =
    useState(false);
  const [openConfirmReportUser, setOpenConfirmReportUser] = useState(false);
  const [openConfirmDeleteComment, setOpenConfirmDeleteComment] =
    useState(false);
  const [openConfirmHideComment, setOpenConfirmHideComment] = useState(false);
  const threeDotRef = useRef();
  useOutsideClick(threeDotRef, () => {
    if (show) setShow(false);
  });

  const handleDeleteComment = async () => {
    try {
      await axios.delete(`/posts/${postId}/comments/${commentId}`);
    } catch (e) {
      setSnackbarMessage({
        type: 'error',
        text: 'コメントの削除が失敗しました',
      });
    }
    setShow(false);
    setOpenConfirmDeleteComment(false);
    refetchPostComments(parentId);
  };

  const handleHideComment = async () => {
    try {
      await axios.post(`/posts/comments/${commentId}/hide`, {
        is_hide: true,
      });
      setSnackbarMessage({
        type: 'success',
        text: 'コメントを非表示しました。',
      });
    } catch (e) {
      setSnackbarMessage({
        type: 'error',
        text: 'コメントの非表示が失敗しました',
      });
    }
    setShow(false);
    setOpenConfirmHideComment(false);
    refetchPostComments(parentId);
  };

  let content: JSX.Element;
  if (isOwnComment) {
    content = (
      <ul className={styles.menuWrapper}>
        <li
          onClick={() => setOpenConfirmDeleteComment(true)}
          style={{ color: '#FA553F' }}
        >
          コメントを削除する
        </li>
      </ul>
    );
  } else {
    content = (
      <ul className={styles.menuWrapper}>
        <li onClick={() => setOpenConfirmReportComment(true)}>
          <Icon name="post-report" width={24} height={24} />
          <span>コメント問題をレポートする</span>
        </li>
        <li onClick={() => setOpenConfirmReportUser(true)}>
          <Icon name="user-report" width={24} height={24} />
          <span>ユーザーをレポートする</span>
        </li>
        {isOwnPost && (
          <li onClick={() => setOpenConfirmHideComment(true)}>
            <Icon name="post-off" width={24} height={24} />
            <span>コメントを非表示にする</span>
          </li>
        )}
      </ul>
    );
  }

  return (
    <div className={styles.threeDotsMenuWrapper} ref={threeDotRef}>
      <div className={styles.triggerWrapper} onClick={() => setShow(!show)}>
        <Icon name="threedots" width={24} height={24} />
      </div>
      <div
        className={styles.contentWrapper}
        style={{
          display: show ? 'block' : 'none',
          width: isOwnComment ? '220px' : '300px',
        }}
      >
        <div className={styles.arrow}></div>
        {content}
      </div>
      {openConfirmDeleteComment && (
        <ConfirmDialog
          open={openConfirmDeleteComment}
          actionCloseFnc={() => setOpenConfirmDeleteComment(false)}
          actionSelectedFnc={handleDeleteComment}
          title="本当に削除しますか？"
        />
      )}
      {openConfirmHideComment && (
        <ConfirmDialog
          open={openConfirmHideComment}
          actionCloseFnc={() => setOpenConfirmHideComment(false)}
          actionSelectedFnc={handleHideComment}
          yesText="非表示する"
          title="本当に非表示しますか？"
        />
      )}
      {openConfirmReportComment && (
        <ConfirmDialog
          open={openConfirmReportComment}
          actionCloseFnc={() => setOpenConfirmReportComment(false)}
          title="オプション選択"
          withTextBox
          textBox={
            <ul className={styles.menuWrapper}>
              <li>
                <a
                  href="https://help.postprime.com/hc/ja/requests/new?ticket_form_id=360008586356"
                  target="_blank"
                  rel="noopener"
                >
                  著作権侵害の報告
                </a>
              </li>
              <li>
                <a
                  href="https://help.postprime.com/hc/ja/requests/new?ticket_form_id=360007972215"
                  target="_blank"
                  rel="noopener"
                >
                  個人情報に関する苦情
                </a>
              </li>
              <li>
                <a
                  href="https://help.postprime.com/hc/ja/requests/new?ticket_form_id=360008586376"
                  target="_blank"
                  rel="noopener"
                >
                  その他の違反報告
                </a>
              </li>
              <li
                style={{ marginTop: '20px' }}
                onClick={() => setOpenConfirmReportComment(false)}
              >
                キャンセル
              </li>
            </ul>
          }
        />
      )}
      {openConfirmReportUser && (
        <ConfirmDialog
          open={openConfirmReportUser}
          actionCloseFnc={() => setOpenConfirmReportUser(false)}
          title="ユーザーをレポートする"
          withTextBox
          textBox={
            <ReportBox
              targetUserId={ownerId}
              onDialogSubmit={() => setOpenConfirmReportUser(false)}
            />
          }
        />
      )}
    </div>
  );
}
